/**
 * Shared popup / badge HTML builders for /explore, used by BOTH the
 * legacy Leaflet path and the MapLibre tile-mode path so the two render
 * byte-identical copy (extracted from explore.tsx without behavior
 * change; same split as dc311-popup.ts / olm-popup.ts, and unit-testable
 * without a map library).
 *
 * ── COLOUR RULES FOR EVERYTHING IN THIS FILE ──────────────────────────
 *
 * Every colour is a `--tt-*` custom property from src/styles/tokens.css.
 * None of them may be a raw hex, and none of them may assume a LIGHT
 * surface. This file used to do both: it emitted `color:#111` bodies,
 * a #333/#444/#555/#666/#777/#888 muted ladder, and chip fills of #eee /
 * #fef3c7 / #f0fdf4 / #fefce8 / #fafaf5 — all light-popup values that
 * rendered on a surface the stylesheet had already themed dark, and on
 * the MapLibre path on a surface the stylesheet had not themed at all.
 *
 * WHY THE STYLESHEET COULD NOT FIX THIS ALONE: `color: inherit` reaches
 * TEXT, never a BACKGROUND. Every chip fill above had to be changed here.
 *
 * A CHIP HAS NO PALETTE OF ITS OWN. It is the raised surface, a 1px
 * border in its own semantic ink, and text in that same ink — so the
 * popup speaks the same four words as the map: amber = litter (and "you
 * can act here"), green = cleaned up, blue = water, violet = impaired.
 * The raised surface is only 1.13:1 against the popup surface, so the
 * border is load-bearing: it is what makes the chip a graphical object
 * that clears SC 1.4.11, and every ink used here measures ≥ 3:1 on the
 * surface (and ≥ 4.5:1 as text, per SC 1.4.3).
 *
 * NEVER use --tt-popup-rule for a chip edge or any other boundary that
 * carries MEANING: it is 1.82:1, legal only for decorative hairlines.
 * NEVER use the map's water blue --tt-water as text (4.34:1, below the
 * 4.5:1 floor) — the text-only tint --tt-ink-water exists for that.
 */

/**
 * Chip = raised surface + 1px border in the chip's own semantic ink +
 * text in that ink. `ink` must be a `--tt-*` var, never a literal.
 */
function chipStyle(ink: string): string {
  return (
    `background:var(--tt-popup-raised);color:${ink};border:1px solid ${ink};` +
    'font-size:10px;padding:2px 6px;border-radius:3px;'
  );
}

import { escapeHtml as escape } from './dc311-popup';
import type { WatershedPopupCopy } from './region-count';
import type { RateEstimate, GoalGradient } from './cleanup-estimator';
import { AMOUNT_BUCKET_LABEL, type CleanupEvent } from './cleanup-types';
import { CLASS_LABEL_BY_VALUE } from './litter-classes';

// Full state names for the "USGS watershed ID {huc12} · {state}" line —
// staff keep the code, everyone else gets a name they recognize.
// Extended to all states for the national tiles; unknown abbreviations
// fall through unchanged.
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
  CA: 'California', CO: 'Colorado', CT: 'Connecticut', DC: 'District of Columbia',
  DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
  IA: 'Iowa', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts',
  MD: 'Maryland', ME: 'Maine', MI: 'Michigan', MN: 'Minnesota',
  MO: 'Missouri', MS: 'Mississippi', MT: 'Montana', NC: 'North Carolina',
  ND: 'North Dakota', NE: 'Nebraska', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NV: 'Nevada', NY: 'New York', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VA: 'Virginia', VT: 'Vermont', WA: 'Washington',
  WI: 'Wisconsin', WV: 'West Virginia', WY: 'Wyoming',
};
export function stateFullName(abbr: string): string {
  return STATE_NAMES[abbr.trim().toUpperCase()] ?? abbr;
}

/**
 * Minimal shape needed from an ATTAINS ImpairmentDoc to decide whether
 * (and how) to render an impairment badge — deliberately loose, same
 * "structural, not the full type" convention as ObsFeatureLike below.
 */
interface ImpairmentBadgeInput {
  impaired_assessment_units?: Array<{ causes?: string[] }> | null;
  top_causes?: Array<{ cause: string }> | null;
}

/**
 * Issue #51: the drinking-water/impairment story previously lived only
 * in the top-of-map banner and the Sources citation drawer — never on
 * the pin popup a user is actually looking at. The impairment data is
 * already loaded in memory per watershed (see explore.tsx's
 * wsImpairmentByHuc); this just renders it as a one-line badge, same
 * visual language as the existing watershed-name badge.
 */
function buildImpairmentBadgeHtml(impairment?: ImpairmentBadgeInput | null): string {
  if (!impairment) return '';
  const isImpaired = Boolean(impairment.impaired_assessment_units?.length);
  if (!isImpaired) return '';
  // TRASH rarely cracks the top-5 causes (the Anacostia's is dominated by
  // E. coli/PCBs while TRASH sits on 4 of 18 AUs), so scan per-AU causes too.
  const hasTrashCause = Boolean(
    impairment.top_causes?.some((c) => c.cause?.toUpperCase().includes('TRASH')) ||
      impairment.impaired_assessment_units?.some((au) =>
        au.causes?.some((c) => c?.toUpperCase().includes('TRASH')),
      ),
  );
  const label = hasTrashCause ? 'EPA-listed impaired for trash' : 'EPA-listed impaired';
  // Impairment ink: the rust-family readable sibling of the trash wash
  // (WATER_STATUS_INK.trash) serves BOTH impairment rungs — the popup
  // says the severity in words, which is stronger anyway.
  return `<span style="${chipStyle('var(--tt-ink-rust)')}">${escape(label)}</span>`;
}

/**
 * Watershed badge for pin popups. Names the containing watershed via
 * the huc12 stamp; the distance line only renders OUTSIDE mapped
 * watersheds and always names its anchor (the ingest centroid,
 * Massaponax Creek). `impairment`, when the containing watershed has
 * EPA ATTAINS impairment data, appends a one-line badge (issue #51) —
 * optional so callers/fixtures that don't have impairment data yet
 * keep rendering byte-identical copy otherwise.
 */
export function buildWsBadgeHtml(opts: {
  wsName: string | null;
  distMi?: unknown;
  bearing?: unknown;
  impairment?: ImpairmentBadgeInput | null;
}): string {
  const { wsName, distMi, bearing, impairment } = opts;
  const impairmentBadge = buildImpairmentBadgeHtml(impairment);
  // "in <watershed>" names a WATER body, so it takes the water ink — the
  // text-only tint, never the map blue (see the file header). The two
  // no-watershed chips are provenance caveats, not one of the four map
  // words, so they take the muted ink rather than inventing a hue.
  return `${wsName
    ? `<span style="${chipStyle('var(--tt-ink-water)')}">in ${escape(wsName)}</span>`
    : `<span style="${chipStyle('var(--tt-popup-ink-muted)')}">outside mapped watersheds</span>${
        distMi != null
          ? `<span style="${chipStyle('var(--tt-popup-ink-muted)')}">${escape(String(distMi))} mi ${bearing ? `${escape(String(bearing))} ` : ''}of Massaponax Creek</span>`
          : ''
      }`}${impairmentBadge}`;
}

/** Minimal structural feature shape (same looseness as dc311-popup). */
interface ObsFeatureLike {
  id?: unknown;
  properties?: Record<string, unknown> | null;
}

/**
 * Cleanup Ledger (P1) render order, per docs/cleanup-ledger-spec.md:
 *   1. trend word + rate band
 *   2. removal line ("neighbors removed ~340 items since March")
 *   3. goal-gradient sentence
 *   4. detail rows (date · amount bucket · display_name or "a neighbor"
 *      · org name when on-behalf-of · verified badge)
 *   5. the verb button
 *
 * The verb button is rendered as an INERT element carrying only a
 * `data-observation-id` attribute — no inline onclick (can't reach
 * ES-module scope from an HTML string). explore.tsx wires the click via
 * a `popupopen` listener on the owning marker, matching the codebase's
 * existing popupopen-for-DOM-lookup idiom rather than introducing a new
 * interactivity pattern.
 *
 * Anchored to the stable observation id, never a merged mound — callers
 * MUST NOT pass this to the renderPileClusters() merged-mound branch
 * (see explore.tsx's isMerged handling).
 */
export function buildCleanupLedgerHtml(
  observationId: string,
  ledger: {
    rate: RateEstimate;
    removalLine: string | null;
    goalGradient: GoalGradient;
    events: CleanupEvent[];
  },
): string {
  const { rate, removalLine, goalGradient, events } = ledger;
  const rateLine = rate.rateBandSentence
    ? `<div style="margin-top:6px;font-weight:600;color:var(--tt-popup-ink);">${escape(capitalize(rate.rateBandSentence))}</div>`
    : `<div style="margin-top:6px;color:var(--tt-popup-ink-muted);font-style:italic;">${escape(capitalize(rate.trendWord))}</div>`;
  const removalHtml = removalLine
    ? `<div style="margin-top:2px;color:var(--tt-popup-ink-muted);">${escape(removalLine)}</div>`
    : '';
  const goalHtml = goalGradient.sentence
    ? `<div style="margin-top:2px;color:var(--tt-popup-ink-muted);">${escape(capitalize(goalGradient.sentence))}</div>`
    : '';
  const detailRows = events.slice(0, 5).map((e) => {
    const who = e.display_name ?? 'a neighbor';
    const onBehalf = e.org_name ? ` on behalf of ${escape(e.org_name)}` : '';
    const verifiedBadge = e.verified
      ? `<span style="${chipStyle('var(--tt-cleanup)')}font-size:9px;padding:1px 5px;margin-left:4px;">verified</span>`
      : '';
    const dateStr = new Date(e.cleaned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `<li>${escape(dateStr)} · ${escape(AMOUNT_BUCKET_LABEL[e.amount_bucket])} · ${escape(who)}${onBehalf}${verifiedBadge}</li>`;
  }).join('');
  const detailsHtml = detailRows
    ? `<ul style="margin:6px 0 0;padding-left:16px;font-size:11px;color:var(--tt-popup-ink-muted);">${detailRows}</ul>`
    : '';
  // `tt-cleanup-ledger` is a stable DOM hook (same convention as
  // `tt-cleanup-verb`): the post-log optimistic refresh and the e2e
  // specs target the ledger block by class, never by styling or copy.
  //
  // The verb button stays GREEN. Amber is the general "you can act here"
  // channel, but this specific action IS the restoration the green ring
  // on the map stands for, so the button and the mark it will create
  // speak the same word. --tt-cleanup measures 13.49:1 on the raised
  // surface as text and 6.9:1 as a border on it, so the button clears
  // both SC 1.4.3 and SC 1.4.11 without a filled background.
  return `
    <div class="tt-cleanup-ledger" style="margin-top:8px;padding:8px;background:var(--tt-popup-raised);border-radius:4px;border:1px solid var(--tt-popup-rule);color:var(--tt-popup-ink);">
      ${rateLine}
      ${removalHtml}
      ${goalHtml}
      ${detailsHtml}
      <button type="button" class="tt-cleanup-verb" data-observation-id="${escape(observationId)}"
        style="margin-top:8px;width:100%;padding:6px 10px;background:var(--tt-popup-surface);color:var(--tt-cleanup);border:1px solid var(--tt-cleanup);border-radius:4px;font-size:12px;font-weight:600;cursor:pointer;">
        I picked some up
      </button>
    </div>`;
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

/**
 * Activate the inert `.tt-cleanup-verb` button inside a freshly-opened
 * popup's DOM node — the wiring half of buildCleanupLedgerHtml's
 * contract above, shared by BOTH map engines (issue #78):
 *
 *   - legacy Leaflet: explore.tsx calls this from its marker
 *     `popupopen` listener with `e.popup.getElement()`;
 *   - tile mode: explore-map-tiles.ts calls it right after
 *     `popup.addTo(map)` with `popup.getElement()`.
 *
 * The heavy flow (bucket-picker overlay, org selector, logCleanupEvent,
 * after-photo step) lives in cleanup-verb.ts and is DYNAMIC-imported at
 * CLICK time, so none of it rides the entry chunk or the maplibre chunk
 * (§10 first-paint budget). A `dataset.wired` guard makes re-wiring the
 * same popup DOM a no-op (Leaflet re-fires popupopen on re-open).
 */
export function wireCleanupVerbButtonIn(
  popupEl: ParentNode | null | undefined,
  host: import('./cleanup-verb').CleanupVerbHost = {},
): void {
  const btn = popupEl?.querySelector?.('.tt-cleanup-verb') as HTMLButtonElement | null;
  if (!btn || btn.dataset.wired) return;
  btn.dataset.wired = '1';
  btn.addEventListener('click', () => {
    const observationId = btn.dataset.observationId;
    if (!observationId) return;
    void import('./cleanup-verb')
      .then(({ openBucketPicker }) => openBucketPicker(observationId, host))
      .catch(() => {
        /* chunk failed to load (e.g. offline) — tapping again retries */
      });
  });
}

/**
 * Structural shape of GET /api/trail/v/{version}/observations/{id}
 * (api.ts's ObservationDetail satisfies it) — same "structural, not the
 * full type" convention as ObsFeatureLike, so this module never imports
 * the API client.
 */
export interface ObsDetailLike {
  photo_count: number;
  label_summary: {
    tier: 'model' | 'owner' | 'consensus';
    items: Array<{ class_label: string; count: number }>;
    total_items: number;
  } | null;
}

/**
 * Tier honesty copy — the headline of the detail block states WHO the
 * item list came from, because a detector guess and a human-verified
 * list must never read the same (the same honesty rule as olmWhenLine's
 * date_basis gate). Unknown tiers fall back to the model line: claiming
 * LESS authority than the data has is the safe direction.
 */
const DETAIL_TIER_COPY: Record<string, string> = {
  model: "Detector's read — not yet human-verified",
  owner: 'Labeled by the reporter',
  consensus: 'Verified by independent reviewers',
};

/**
 * "What's here" detail block for a user-submission pin popup (hotspot
 * drill-down). Pure HTML builder over the /observations/{id} payload —
 * no fetch, no DOM — so it is unit-testable exactly like the other
 * builders in this file. The ASYNC half (fetch → swap into the
 * `.tt-obs-detail` stub) lives with the popup wiring in
 * explore-map-tiles.ts, same split as buildCleanupLedgerHtml /
 * wireCleanupVerbButtonIn.
 *
 * Copy rules:
 *   - The item list is styled like the existing tag_breakdown block
 *     (amber left rule = litter composition) and headed by the tier
 *     honesty line above.
 *   - Items render as "N × <friendly label>" via CLASS_LABEL_BY_VALUE
 *     (wire values are snake_case `category.object` — AGENTS.md: never
 *     show raw enum values); unknown classes fall back to the raw
 *     wire label rather than being dropped.
 *   - `huc12` gates the review CTA: /label's watershed scoping needs a
 *     watershed to scope to. A plain anchor is correct — /label is a
 *     separate page, and the map's state survives in the URL.
 */
export function buildObsDetailHtml(
  detail: ObsDetailLike,
  opts: { huc12: string | null },
): string {
  const s = detail.label_summary;
  let summaryHtml = '';
  if (s && s.items.length > 0) {
    const tierLine = DETAIL_TIER_COPY[s.tier] ?? DETAIL_TIER_COPY.model;
    const listedSum = s.items.reduce((n, it) => n + it.count, 0);
    const moreCount = s.total_items - listedSum;
    const rows = s.items
      .map(
        (it) =>
          `<li>${escape(String(it.count))} × ${escape(CLASS_LABEL_BY_VALUE[it.class_label] ?? it.class_label)}</li>`,
      )
      .join('');
    summaryHtml = `
      <div style="margin-top: 6px; padding: 6px 8px; background: var(--tt-popup-raised); border-left: 2px solid var(--tt-litter); font-size: 11px; color: var(--tt-popup-ink);">
        <div style="font-weight: 600; color: var(--tt-litter); margin-bottom: 3px;">${escape(tierLine)}</div>
        <ul style="margin: 0; padding-left: 16px;">
          ${rows}
          ${moreCount > 0 ? `<li>+ ${escape(String(moreCount))} more</li>` : ''}
        </ul>
      </div>`;
  }
  let photosHtml = '';
  if (detail.photo_count > 0) {
    const cta = opts.huc12
      ? `<div style="margin-top: 4px;"><a href="/label?huc12=${escape(encodeURIComponent(opts.huc12))}" style="color: var(--tt-popup-link); font-weight: 500; text-decoration: underline;">Review photos &amp; fix labels</a></div>`
      : '';
    photosHtml = `
      <div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">
        ${escape(String(detail.photo_count))} photo${detail.photo_count === 1 ? '' : 's'} of this spot
        ${cta}
      </div>`;
  }
  return `${summaryHtml}${photosHtml}`;
}

/**
 * The generic (non-dc311) pin popup. Byte-identical to the template
 * that previously lived inline in explore.tsx's marker loop, plus an
 * optional `ledgerHtml` block (Cleanup Ledger P1) rendered ABOVE the
 * item-tally breakdown — the ledger's trend/rate line is the new
 * headline; the raw item count is demoted to supporting detail (issue
 * #48: a raw item tally as the lead line contradicts the product's own
 * "we don't just count trash" positioning).
 *
 * `wsBadgeHtml` is pre-built (already escaped) by buildWsBadgeHtml;
 * `whenLine` is olmWhenLine(p) for OLM pins, the relative age string
 * otherwise; `styleLabel` is SOURCE_STYLE[key].label.
 *
 * REPORT LINK (App Store Guideline 1.2b): user-contributed pins — and
 * only those, gated on the `sub-` id prefix — carry a "Report this pin"
 * link to /report/<id>. It is a plain anchor rather than a wired button
 * because a popup is an HTML string with no route into module scope
 * (the .tt-cleanup-verb dance above is what wiring one costs, and the
 * report flow needs a page anyway on the way in from a signed-out tap).
 * OLM / 311 / agency pins get nothing: we do not host that content and
 * cannot take it down, so offering the control would be a dead end.
 *
 * `alreadyReported` replaces that link with a status line. A pin this
 * device has reported is normally not drawn at all (reported-pins.ts +
 * the collectViewport filter), so this is the belt to that brace — the
 * legacy renderer, a stale buffer, a hand-built popup. Re-offering
 * "Report this pin" on content the user already reported is the exact
 * loop that made reporting look inert.
 */
export function buildGenericObsPopupHtml(
  feature: ObsFeatureLike,
  opts: {
    styleLabel: string;
    wsBadgeHtml: string;
    isMine: boolean;
    whenLine: string;
    /** Cleanup Ledger P1 block — omitted for merged mounds (no stable id). */
    ledgerHtml?: string;
    /**
     * "What's here" detail slot (hotspot drill-down), rendered AFTER the
     * tag_breakdown block. Pre-built (already escaped) HTML: the tile-mode
     * path passes a `.tt-obs-detail` loading stub here and swaps
     * buildObsDetailHtml output into it post-fetch. Default '' keeps every
     * existing caller byte-identical.
     */
    detailSlotHtml?: string;
    /** This device has already reported this pin — see the docblock. */
    alreadyReported?: boolean;
  },
): string {
  const p = (feature.properties ?? {}) as Record<string, any>;
  const d = (p._display ?? {}) as Record<string, any>;
  const thumb = p.photo_url
    ? `<div style="margin-top:8px;"><img src="${escape(p.photo_url)}" alt="" style="width:100%;max-width:260px;border-radius:4px;"/></div>`
    : '';
  const obsId = typeof feature.id === 'string' ? feature.id : '';
  const reportHtml = !obsId.startsWith('sub-')
    ? ''
    : opts.alreadyReported
      ? `<div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">You reported this — it is out of public view while we review it.</div>`
      : `<div style="margin-top: 6px;"><a href="/report/${escape(encodeURIComponent(obsId))}" style="color: var(--tt-popup-ink-muted); font-size: 11px; text-decoration: underline;">Report this pin</a></div>`;
  return `
          <div style="font: 12px/1.45 system-ui, sans-serif; color: var(--tt-popup-ink); max-width: 280px;">
            <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
              <span style="font-weight: 600; text-transform: capitalize; color: var(--tt-litter);">
                ${escape(String(p.category ?? 'other').replace(/_/g, ' '))}
              </span>
              <span style="${chipStyle('var(--tt-popup-ink-muted)')}">${opts.styleLabel}</span>
              ${opts.wsBadgeHtml}
            </div>
            ${opts.ledgerHtml ?? ''}
            ${opts.isMine ? `<div style="margin-top:6px;${chipStyle('var(--tt-litter)')}font-size:11px;font-weight:600;padding:3px 8px;display:inline-block;">Your report — pending verification</div>` : ''}
            ${d.title ? `<div style="margin-top: 6px; font-weight: 500;">${escape(d.title)}</div>` : ''}
            ${d.place_name && !d.title?.includes(d.place_name) ? `<div style="margin-top: 4px; color: var(--tt-popup-ink-muted); font-size: 11px;">📍 ${escape(d.place_name)}</div>` : ''}
            ${d.snippet ? `<div style="margin-top: 6px; font-style: italic; color: var(--tt-popup-ink-muted); border-left: 2px solid var(--tt-popup-rule); padding-left: 8px;">&ldquo;${escape(d.snippet)}&rdquo;</div>` : ''}
            ${d.tag_breakdown && d.tag_breakdown.top_tags?.length ? `
              <div style="margin-top: 6px; padding: 6px 8px; background: var(--tt-popup-raised); border-left: 2px solid var(--tt-litter); font-size: 11px; color: var(--tt-popup-ink);">
                <div style="font-weight: 600; color: var(--tt-litter); margin-bottom: 3px;">
                  ${Number(d.tag_breakdown.total_items).toLocaleString()} item${d.tag_breakdown.total_items === 1 ? '' : 's'} photographed across ${d.tag_breakdown.photos_sampled} photo${d.tag_breakdown.photos_sampled === 1 ? '' : 's'}
                </div>
                <ul style="margin: 0; padding-left: 16px;">
                  ${d.tag_breakdown.top_tags.map((t: any) => `<li>${escape(String(t.count))} × ${escape(t.label)}</li>`).join('')}
                </ul>
                ${d.olm_drilldown_url ? `<div style="margin-top: 4px;"><a href="${escape(d.olm_drilldown_url)}" target="_blank" rel="noopener" style="color: var(--tt-popup-link); font-weight: 500; text-decoration: underline;">View photos on OpenLitterMap ↗</a></div>` : ''}
              </div>` : (d.olm_drilldown_url ? `<div style="margin-top: 6px; padding: 6px 8px; background: var(--tt-popup-raised); border-left: 2px solid var(--tt-popup-rule); font-size: 11px; color: var(--tt-popup-ink-muted);">No per-item tag data available for this cluster's bbox.<br/><a href="${escape(d.olm_drilldown_url)}" target="_blank" rel="noopener" style="color: var(--tt-popup-link); font-weight: 500; text-decoration: underline;">View photos on OpenLitterMap ↗</a></div>` : '')}
            ${opts.detailSlotHtml ?? ''}
            ${thumb}
            <div style="margin-top: 6px; color: var(--tt-popup-ink-muted);">
              ${opts.whenLine} · ${escape(p.source?.attribution ?? '')}
            </div>
            ${d.permalink ? `<div style="margin-top: 4px;"><a href="${escape(d.permalink)}" target="_blank" rel="noopener" style="color: var(--tt-popup-link); text-decoration: underline;">view source ↗</a></div>` : ''}
            ${d.place_query ? `<div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">📍 Geocoded from &ldquo;${escape(d.place_query)}&rdquo; — coordinates approximate</div>` : ''}
            ${p.context?.drains_to_intake ? `<div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">This spot drains toward: ${escape(p.context.drains_to_intake)}</div>` : ''}
            ${reportHtml}
          </div>
        `;
}

/**
 * Watershed polygon-click popup shell. The counting semantics live in
 * buildWatershedPopupCopy (region-count.ts); this renders the copy into
 * the same HTML shell the legacy Leaflet popup used.
 *
 * THIS IS THE WORST FORM OF THE FOUNDER'S COMPLAINT and the one measured
 * live on beta: it set no `color` at all, so on the MapLibre path it
 * inherited cream text onto MapLibre's un-themed white default at
 * 1.24:1, and every secondary line was additionally knocked back with
 * `opacity: 0.6–0.8` to 1.13–1.19:1. Both are fixed the same way: state
 * the ink explicitly from the tokens, and express "secondary" as a
 * SEPARATE TOKEN rather than as opacity — opacity multiplies whatever
 * contrast the surface happens to give you, which is how a legible
 * colour silently becomes an illegible one.
 */
export function buildWatershedPopupShellHtml(args: {
  name: string;
  huc12: string;
  state: string;
  copy: WatershedPopupCopy;
  /**
   * Pre-built year-bar strip HTML (year-bars.ts's buildYearBarHtml) —
   * optional so the tile-mode path (which doesn't yet wire a
   * member-record query into its popup) keeps compiling and rendering
   * byte-identical copy until it opts in.
   */
  yearBarHtml?: string;
}): string {
  const { name, huc12, state, copy, yearBarHtml } = args;
  const muted = 'color: var(--tt-popup-ink-muted); font-size: 11px;';
  return `
          <div style="font: 12px/1.45 system-ui, sans-serif; min-width: 200px; color: var(--tt-popup-ink);">
            <div style="font-weight: 600; font-size: 13px; color: var(--tt-ink-water);">${escape(name)}</div>
            <div style="${muted} margin-top: 2px;">
              USGS watershed ID ${escape(huc12)}${state ? ` · ${escape(stateFullName(state))}` : ''}
            </div>
            <div style="margin-top: 8px; font-weight: 600;">
              ${escape(copy.countLine)}
            </div>
            ${copy.isEmpty ? `<div style="${muted} margin-top: 4px;">Tap the &ldquo;Report litter&rdquo; button below to add the first report.</div>` : ''}
            ${copy.showingLine ? `<div style="${muted} margin-top: 4px;">${escape(copy.showingLine)}</div>` : ''}
            ${copy.straddleLine ? `<div style="${muted} margin-top: 4px;">${escape(copy.straddleLine)}</div>` : ''}
            ${yearBarHtml ?? ''}
            <div style="${muted} margin-top: 8px;">
              Tap &#9432; (bottom right) for this watershed&rsquo;s citations.
            </div>
            <div style="color: var(--tt-popup-ink-muted); font-size: 10px; margin-top: 6px;">
              ${escape(copy.basisLine)}
            </div>
          </div>
        `;
}

/**
 * Relative age string shared by both map paths (same thresholds the
 * legacy marker loop used inline).
 */
export function relativeAgeString(ageDays: number): string {
  return ageDays === 0 ? 'today' :
    ageDays === 1 ? 'yesterday' :
    ageDays < 30 ? `${ageDays} days ago` :
    ageDays < 365 ? `${Math.floor(ageDays / 30)} mo ago` :
    `${Math.floor(ageDays / 365)} yr ago`;
}
